import * as React from "react";
import {useContext} from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {AuthContext} from "../components/AuthContext";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import {useSnackbar} from "../components/SnackbarContext";
import {submitTradingFeedback} from "../apis/junkyard";
import {TradingFeedbackRequest} from "../utils/typesTrading";
import AppTheme from "../themes/AppTheme";
import {TextareaAutosize} from "@mui/material";

export const FeedbackPage: React.FC = () => {
    const authContext = useContext(AuthContext);
    const {showSnackbar} = useSnackbar();

    const [feedback, setFeedback] = React.useState<string>("");
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

    const isAuthenticated = authContext.isAuthenticated();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const trimmedFeedback = feedback.trim();
        if (!trimmedFeedback) {
            showSnackbar("Please enter your feedback before submitting.", "warning");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await submitTradingFeedback({
                access_token: authContext.readAccessToken(),
                feedback: trimmedFeedback,
            } as TradingFeedbackRequest);

            if (response.success) {
                showSnackbar("Thanks for your feedback!", "success");
            } else {
                showSnackbar("Could not capture feedback at this time", "error");
            }
        } catch (error) {
            console.error("Failed to submit feedback", error);
            showSnackbar("Failed to submit feedback. Please try again.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <AppAppBar />
            <Box
                sx={{
                    mt: 16,
                    px: 2,
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    minHeight: "80vh",
                }}
            >
                <Card sx={{maxWidth: 720, width: "100%", height: "fit-content"}}>
                    <CardContent>
                        <Stack spacing={6}>
                            <Box>
                                <Typography variant="h5" component="h1" gutterBottom>
                                    Feedback
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tell us what is working well, what is confusing, or what you want next.
                                </Typography>
                            </Box>

                            {!isAuthenticated && (
                                <Alert severity="info">
                                    You are not signed in. Sign in first so we can associate feedback with your account.
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <Stack spacing={2}>
                                    <Box>
                                        <TextareaAutosize
                                            aria-label="minimum height"
                                            onChange={(event) => setFeedback(event.target.value)}
                                            minRows={3}
                                            placeholder="Share product feedback, bugs, or feature ideas..."
                                            style={{ width: "100%" }}
                                        />                                        {/*<TextField*/}
                                    </Box>
                                    <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={!isAuthenticated || isSubmitting}
                                            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
                                        >
                                            {isSubmitting ? "Submitting..." : "Submit feedback"}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
            <Footer />
        </AppTheme>
    );
};


